import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { X, Camera, Flashlight } from "@phosphor-icons/react";

export default function CameraScanDialog({ open, onClose, onResult }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const [error, setError] = useState("");
  const [last, setLast] = useState("");
  const [torchOn, setTorchOn] = useState(false);
  const [hasTorch, setHasTorch] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    const reader = new BrowserMultiFormatReader();
    setError("");
    setLast("");

    (async () => {
      try {
        const devices = await BrowserMultiFormatReader.listVideoInputDevices();
        if (!devices.length) {
          setError("No camera detected on this device.");
          return;
        }
        // Prefer back camera if available
        const back = devices.find((d) => /back|rear|environment/i.test(d.label));
        const deviceId = (back || devices[0]).deviceId;

        const controls = await reader.decodeFromVideoDevice(
          deviceId,
          videoRef.current,
          (result, err) => {
            if (!active) return;
            if (result) {
              const text = result.getText();
              setLast(text);
              onResult(text);
              cleanup();
            }
          }
        );
        controlsRef.current = controls;

        // Torch capability detection
        const stream = videoRef.current?.srcObject;
        const track = stream?.getVideoTracks?.()[0];
        const caps = track?.getCapabilities?.();
        if (caps && caps.torch) setHasTorch(true);
      } catch (e) {
        setError(
          e?.name === "NotAllowedError"
            ? "Camera permission denied. Enable camera access in your browser."
            : e?.message || "Could not start the camera."
        );
      }
    })();

    const cleanup = () => {
      active = false;
      try {
        controlsRef.current?.stop();
      } catch (err) {
        console.warn("Camera scanner cleanup: controls.stop() failed", err);
      }
      const stream = videoRef.current?.srcObject;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    };

    return cleanup;
    // eslint-disable-next-line
  }, [open]);

  const toggleTorch = async () => {
    const stream = videoRef.current?.srcObject;
    const track = stream?.getVideoTracks?.()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(!torchOn);
    } catch (err) {
      console.warn("Camera torch toggle not supported on this device", err);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-[#1A2332]/80 flex items-start justify-center pt-4 sm:pt-10 md:pt-16 px-3 pb-3 overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-zinc-300 w-full max-w-lg"
        data-testid="scan-dialog"
      >
        <div className="flex items-center justify-between border-b border-zinc-300 px-6 py-4 bg-[#1A2332] text-white">
          <div className="flex items-center gap-2">
            <Camera size={18} weight="duotone" />
            <h2 className="font-display text-lg font-semibold">Scan barcode / QR</h2>
          </div>
          <div className="flex items-center gap-1">
            {hasTorch && (
              <button
                type="button"
                onClick={toggleTorch}
                data-testid="scan-torch-button"
                className="p-1.5 hover:bg-[#243044]"
                title={torchOn ? "Torch off" : "Torch on"}
              >
                {torchOn ? <Flashlight size={16} weight="fill" /> : <Flashlight size={16} weight="bold" />}
              </button>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-[#243044]" data-testid="scan-dialog-close">
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>

        <div className="bg-black aspect-video relative">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
            data-testid="scan-video"
          />
          {/* Reticle */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="w-3/4 h-1/2 border-2 border-[#FF9800] relative">
              <div className="absolute -top-px -left-px w-6 h-6 border-t-4 border-l-4 border-emerald-400" />
              <div className="absolute -top-px -right-px w-6 h-6 border-t-4 border-r-4 border-emerald-400" />
              <div className="absolute -bottom-px -left-px w-6 h-6 border-b-4 border-l-4 border-emerald-400" />
              <div className="absolute -bottom-px -right-px w-6 h-6 border-b-4 border-r-4 border-emerald-400" />
              <div className="absolute left-0 right-0 top-1/2 h-px bg-red-500/70 animate-pulse" />
            </div>
          </div>
          {error && (
            <div className="absolute inset-0 flex items-center justify-center px-6">
              <div className="bg-red-600 text-white p-4 text-sm text-center max-w-xs">{error}</div>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-zinc-300 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            {last ? (
              <span className="text-emerald-700 font-mono">DETECTED · {last}</span>
            ) : (
              "Point the camera at a SKU barcode or QR code"
            )}
          </div>
          <button
            onClick={onClose}
            className="text-xs font-bold uppercase tracking-widest text-zinc-700 hover:text-zinc-900"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
